import { CELL_SIZE, COLS, ROWS, TANK_PADDING, VIEW_HEIGHT, VIEW_WIDTH } from './constants';
import { TileType, type Rect, type TankEntity, type Vec2 } from './types';
import { clamp, rectsOverlap } from './utils';

export function boundsAt(x: number, y: number, size: number, padding = 0): Rect {
  const half = size / 2 - padding;
  return {
    left: x - half,
    right: x + half,
    top: y - half,
    bottom: y + half,
  };
}

export function tankBoundsAt(x: number, y: number, size: number): Rect {
  return boundsAt(x, y, size, TANK_PADDING);
}

export function tileBlocksTank(tile: TileType): boolean {
  return tile === TileType.Brick || tile === TileType.Metal || tile === TileType.Water || tile === TileType.BaseCore || tile === TileType.BaseWall || tile === TileType.ReinforcedWall;
}

export function tileBlocksBullet(tile: TileType): boolean {
  return tile === TileType.Brick || tile === TileType.Metal || tile === TileType.BaseCore || tile === TileType.BaseWall || tile === TileType.ReinforcedWall;
}

export function cellsForRect(rect: Rect): { startCol: number; endCol: number; startRow: number; endRow: number } {
  return {
    startCol: Math.floor(clamp(rect.left, 0, VIEW_WIDTH - 1) / CELL_SIZE),
    endCol: Math.floor(clamp(rect.right - 0.01, 0, VIEW_WIDTH - 1) / CELL_SIZE),
    startRow: Math.floor(clamp(rect.top, 0, VIEW_HEIGHT - 1) / CELL_SIZE),
    endRow: Math.floor(clamp(rect.bottom - 0.01, 0, VIEW_HEIGHT - 1) / CELL_SIZE),
  };
}

export function rectHitsTankTile(map: TileType[][], rect: Rect): boolean {
  if (rect.left < 0 || rect.right > VIEW_WIDTH || rect.top < 0 || rect.bottom > VIEW_HEIGHT) return true;
  const cells = cellsForRect(rect);
  for (let row = cells.startRow; row <= cells.endRow; row += 1) {
    for (let col = cells.startCol; col <= cells.endCol; col += 1) {
      if (row < 0 || row >= ROWS || col < 0 || col >= COLS) return true;
      if (tileBlocksTank(map[row][col])) return true;
    }
  }
  return false;
}

export function canTankOccupy(map: TileType[][], x: number, y: number, size: number): boolean {
  return !rectHitsTankTile(map, tankBoundsAt(x, y, size));
}

export function collidesWithAnyTank(rect: Rect, tanks: TankEntity[], ignoreId?: string): TankEntity | null {
  for (const tank of tanks) {
    if (!tank.alive || tank.id === ignoreId) continue;
    if (rectsOverlap(rect, tankBoundsAt(tank.x, tank.y, tank.size))) return tank;
  }
  return null;
}

export function moveTankWithCollision(tank: TankEntity, map: TileType[][], tanks: TankEntity[], dx: number, dy: number): void {
  if (!tank.alive) return;
  if (dx !== 0) {
    const nx = tank.x + dx;
    const predicted = tankBoundsAt(nx, tank.y, tank.size);
    if (!rectHitsTankTile(map, predicted) && !collidesWithAnyTank(predicted, tanks, tank.id)) {
      tank.x = nx;
    } else if (Math.abs(dx) > 0.001) {
      const snapped = snapNearGrid(tank.x, tank.y);
      if (Math.abs(snapped.y - tank.y) <= 5 && canTankOccupy(map, tank.x, snapped.y, tank.size)) tank.y = snapped.y;
      tank.vx = 0;
      tank.stuckTimer += 1 / 60;
    }
  }

  if (dy !== 0) {
    const ny = tank.y + dy;
    const predicted = tankBoundsAt(tank.x, ny, tank.size);
    if (!rectHitsTankTile(map, predicted) && !collidesWithAnyTank(predicted, tanks, tank.id)) {
      tank.y = ny;
    } else if (Math.abs(dy) > 0.001) {
      const snapped = snapNearGrid(tank.x, tank.y);
      if (Math.abs(snapped.x - tank.x) <= 5 && canTankOccupy(map, snapped.x, tank.y, tank.size)) tank.x = snapped.x;
      tank.vy = 0;
      tank.stuckTimer += 1 / 60;
    }
  }

  tank.x = clamp(tank.x, tank.size / 2, VIEW_WIDTH - tank.size / 2);
  tank.y = clamp(tank.y, tank.size / 2, VIEW_HEIGHT - tank.size / 2);
}

export function resolveTankOverlaps(tanks: TankEntity[], map: TileType[][]): void {
  const live = tanks.filter((tank) => tank.alive);
  for (let pass = 0; pass < 3; pass += 1) {
    for (let i = 0; i < live.length; i += 1) {
      for (let j = i + 1; j < live.length; j += 1) {
        const a = live[i];
        const b = live[j];
        const ab = tankBoundsAt(a.x, a.y, a.size);
        const bb = tankBoundsAt(b.x, b.y, b.size);
        if (!rectsOverlap(ab, bb)) continue;

        const overlapX = Math.min(ab.right - bb.left, bb.right - ab.left);
        const overlapY = Math.min(ab.bottom - bb.top, bb.bottom - ab.top);
        const pushA = a.team === 'player' && b.team === 'enemy' ? 0.55 : 0.5;
        const pushB = 1 - pushA;

        if (overlapX < overlapY) {
          const sign = a.x < b.x ? -1 : 1;
          tryMoveResolved(a, map, sign * overlapX * pushA, 0);
          tryMoveResolved(b, map, -sign * overlapX * pushB, 0);
        } else {
          const sign = a.y < b.y ? -1 : 1;
          tryMoveResolved(a, map, 0, sign * overlapY * pushA);
          tryMoveResolved(b, map, 0, -sign * overlapY * pushB);
        }
        a.vx *= 0.2;
        a.vy *= 0.2;
        b.vx *= 0.2;
        b.vy *= 0.2;
      }
    }
  }
}

export function findSafeSpawn(origin: Vec2, map: TileType[][], tanks: TankEntity[], size: number): Vec2 {
  const offsets = [
    [0, 0],
    [-CELL_SIZE, 0],
    [CELL_SIZE, 0],
    [0, -CELL_SIZE],
    [0, CELL_SIZE],
    [-CELL_SIZE, -CELL_SIZE],
    [CELL_SIZE, -CELL_SIZE],
    [-CELL_SIZE, CELL_SIZE],
    [CELL_SIZE, CELL_SIZE],
    [-CELL_SIZE * 2, 0],
    [CELL_SIZE * 2, 0],
  ];
  for (const [ox, oy] of offsets) {
    const x = clamp(origin.x + ox, size / 2, VIEW_WIDTH - size / 2);
    const y = clamp(origin.y + oy, size / 2, VIEW_HEIGHT - size / 2);
    const rect = tankBoundsAt(x, y, size);
    if (!rectHitsTankTile(map, rect) && !collidesWithAnyTank(rect, tanks)) return { x, y };
  }
  return origin;
}

function tryMoveResolved(tank: TankEntity, map: TileType[][], dx: number, dy: number): void {
  const nx = clamp(tank.x + dx, tank.size / 2, VIEW_WIDTH - tank.size / 2);
  const ny = clamp(tank.y + dy, tank.size / 2, VIEW_HEIGHT - tank.size / 2);
  if (canTankOccupy(map, nx, ny, tank.size)) {
    tank.x = nx;
    tank.y = ny;
  }
}

function snapNearGrid(x: number, y: number): Vec2 {
  const snap = (value: number) => Math.round((value - CELL_SIZE / 2) / CELL_SIZE) * CELL_SIZE + CELL_SIZE / 2;
  return {
    x: snap(x),
    y: snap(y),
  };
}
